"use client";

import { useEffect, useState } from "react";
import type { UserTagSuggestion } from "@/components/posts/user-tag-picker";
import { Label } from "@/components/ui/label";
import {
  ModeToggle,
  type Participant,
  suggestionToParticipant,
  TeamCard,
  ValidationWarning,
} from "./base-match-template";
import { ActivityCourtPreview, ActivityDisplayPreview } from "./previews";
import { SnookerScoreEditor } from "./score-editors/snooker-score-editor";

// ──────────────────────────────────────────────────────────────────────────────
// TYPES
// ──────────────────────────────────────────────────────────────────────────────

export type SnookerTemplateData = {
  mode: "singles" | "doubles";
  matchScore: { yourFrames: number; opponentFrames: number };
  /** Optional per-frame scores (points or 1–0 if you only tracked the winner). */
  frames?: Array<{ yourScore: number; opponentScore: number }>;
  highestBreak?: number;
  positions: Record<string, "your-team" | "opponent-team">;
  hasScore?: boolean;
};

export type SnookerParticipant = Participant;

// ──────────────────────────────────────────────────────────────────────────────
// INPUT TEMPLATE
// ──────────────────────────────────────────────────────────────────────────────

type SnookerTemplateProps = {
  currentUser: SnookerParticipant;
  onChange: (
    data: SnookerTemplateData,
    participants: SnookerParticipant[],
  ) => void;
  initialData?: Partial<SnookerTemplateData>;
};

export function SnookerTemplate({
  currentUser,
  onChange,
  initialData,
}: SnookerTemplateProps) {
  const [mode, setMode] = useState<SnookerTemplateData["mode"]>(
    initialData?.mode ?? "singles",
  );
  const [participants, setParticipants] = useState<SnookerParticipant[]>([
    currentUser,
  ]);
  const [teams, setTeams] = useState<
    Record<string, "your-team" | "opponent-team">
  >(() => ({
    [currentUser.id]: "your-team",
    ...(initialData?.positions ?? {}),
  }));
  type SnookerScorePart = Pick<
    SnookerTemplateData,
    "matchScore" | "frames" | "highestBreak"
  >;
  const [scorePart, setScorePart] = useState<SnookerScorePart>(() => ({
    matchScore: initialData?.matchScore ?? { yourFrames: 0, opponentFrames: 0 },
    frames: initialData?.frames ?? [],
    highestBreak:
      typeof initialData?.highestBreak === "number" &&
      initialData.highestBreak > 0
        ? initialData.highestBreak
        : undefined,
  }));

  const maxPerTeam = mode === "doubles" ? 2 : 1;

  const yourTeam = participants.filter((p) => teams[p.id] === "your-team");
  const opponentTeam = participants.filter(
    (p) => teams[p.id] === "opponent-team",
  );
  const selectedUserIds = new Set(participants.map((p) => p.id));

  useEffect(() => {
    onChange(
      {
        mode,
        matchScore: scorePart.matchScore,
        frames: scorePart.frames ?? [],
        highestBreak: scorePart.highestBreak,
        positions: teams,
        hasScore: true,
      },
      participants,
    );
  }, [mode, scorePart, teams, participants, onChange]);

  const handleAddParticipant = (
    suggestion: UserTagSuggestion,
    team: "your-team" | "opponent-team",
  ) => {
    const teamCount =
      team === "your-team" ? yourTeam.length : opponentTeam.length;
    if (teamCount >= maxPerTeam) return;

    const newParticipant = suggestionToParticipant(suggestion);
    setParticipants((prev) => [...prev, newParticipant]);
    setTeams((prev) => ({ ...prev, [suggestion.id]: team }));
  };

  const handleRemoveParticipant = (userId: string) => {
    if (userId === currentUser.id) return;
    setParticipants((prev) => prev.filter((p) => p.id !== userId));
    setTeams((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  };

  const yourFrames = scorePart.matchScore.yourFrames;
  const opponentFrames = scorePart.matchScore.opponentFrames;
  const yourWon = yourFrames > opponentFrames;
  const opponentWon = opponentFrames > yourFrames;

  const isYourTeamComplete = yourTeam.length === maxPerTeam;
  const isOpponentComplete = opponentTeam.length === maxPerTeam;
  const isComplete = isYourTeamComplete && isOpponentComplete;

  return (
    <div className="space-y-6">
      <ModeToggle
        label="Match Mode"
        value={mode}
        onChange={(m) => setMode(m as "singles" | "doubles")}
        options={[
          { value: "singles", label: "Singles (1v1)" },
          { value: "doubles", label: "Doubles (2v2)" },
        ]}
      />

      <div className="grid grid-cols-2 gap-3">
        <TeamCard
          label="Team 1"
          isYourTeam
          players={yourTeam}
          maxPlayers={maxPerTeam}
          currentUserId={currentUser.id}
          selectedUserIds={selectedUserIds}
          onAddPlayer={(s) => handleAddParticipant(s, "your-team")}
          onRemovePlayer={handleRemoveParticipant}
          highlight={yourWon}
          isComplete={isYourTeamComplete}
        />
        <TeamCard
          label="Team 2"
          isYourTeam={false}
          players={opponentTeam}
          maxPlayers={maxPerTeam}
          currentUserId={currentUser.id}
          selectedUserIds={selectedUserIds}
          onAddPlayer={(s) => handleAddParticipant(s, "opponent-team")}
          onRemovePlayer={handleRemoveParticipant}
          highlight={opponentWon}
          isComplete={isOpponentComplete}
        />
      </div>

      {!isComplete && (
        <ValidationWarning
          message={
            !isYourTeamComplete && !isOpponentComplete
              ? "Add players to both sides to post"
              : !isOpponentComplete
                ? `Add ${maxPerTeam - opponentTeam.length} more opponent${maxPerTeam - opponentTeam.length > 1 ? "s" : ""}`
                : ""
          }
        />
      )}

      <SnookerScoreEditor
        initialData={initialData as unknown as Record<string, unknown>}
        onChange={(data) => setScorePart(data as unknown as SnookerScorePart)}
      />

      {participants.length >= 2 && (
        <div className="space-y-2">
          <Label className="text-text-primary">Preview</Label>
          <ActivityCourtPreview
            activityId="snooker"
            yourTeam={yourTeam}
            opponentTeam={opponentTeam}
            yourWon={yourWon}
          />
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// DISPLAY COMPONENT
// ──────────────────────────────────────────────────────────────────────────────

type SnookerDisplayProps = {
  data: SnookerTemplateData | null;
  participants: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }[];
};

export function SnookerDisplay({ data, participants }: SnookerDisplayProps) {
  return (
    <ActivityDisplayPreview
      activityId="snooker"
      data={data}
      participants={participants}
    />
  );
}
